import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNotificationStore } from '@stores/notificationStore';
import { apiClient } from '@lib/api';
import { availableFlagKeys } from '@lib/flags';

interface Flag {
  flag_key: string;
  enabled: boolean;
  rollout_bucket?: number | null;
}

interface FeatureFlagsManagerProps {
  installId: string;
  flags: Flag[];
  onFlagsChange?: () => void;
  showManage?: boolean;
  onManageToggle?: (show: boolean) => void;
  /** App id used to offer the not-yet-added canonical flag keys as a dropdown. */
  appId?: string;
}

export const FeatureFlagsManager: React.FC<FeatureFlagsManagerProps> = ({
  installId,
  flags,
  onFlagsChange,
  showManage = false,
  onManageToggle,
  appId,
}) => {
  const { addNotification } = useNotificationStore();
  const [newFlag, setNewFlag] = useState('');
  const [isManaging, setIsManaging] = useState(showManage);

  // Canonical flag catalog for the app, when the caller knows the app.
  const { data: catalogFlags, isLoading: catalogLoading } = useQuery({
    queryKey: ['app-flags', appId],
    queryFn: async () => {
      const response = await apiClient.get<any[]>(`/api/apps/${appId}/flags`);
      return Array.isArray(response) ? response : [];
    },
    enabled: !!appId,
  });

  const unaddedKeys = appId ? availableFlagKeys(catalogFlags, flags) : [];

  // Keep the selection on a not-yet-added key (first one by default).
  useEffect(() => {
    if (appId && !catalogLoading && unaddedKeys.length > 0 && !unaddedKeys.includes(newFlag)) {
      setNewFlag(unaddedKeys[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, catalogLoading, unaddedKeys.join('|')]);

  const handleToggle = () => {
    setIsManaging(!isManaging);
    onManageToggle?.(!isManaging);
  };

  const handleAddFlag = async () => {
    if (!newFlag.trim()) return;
    try {
      await apiClient.post(`/api/installs/${installId}/flags`, { flag_key: newFlag });
      setNewFlag('');
      onFlagsChange?.();
      addNotification({
        message: `Flag ${newFlag} added successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add flag';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to add flag:', error);
    }
  };

  const handleToggleFlag = async (flagKey: string, currentEnabled: boolean) => {
    try {
      await apiClient.put(`/api/installs/${installId}/flags/${flagKey}/toggle`, {
        enabled: !currentEnabled,
      });
      onFlagsChange?.();
      addNotification({
        message: `Flag ${flagKey} ${!currentEnabled ? 'enabled' : 'disabled'} successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle flag';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to toggle flag:', error);
    }
  };

  const handleRemoveFlag = async (flagKey: string) => {
    if (!window.confirm(`Remove ${flagKey}?`)) return;
    try {
      await apiClient.delete(`/api/installs/${installId}/flags/${flagKey}`);
      onFlagsChange?.();
      addNotification({
        message: `Flag ${flagKey} removed successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove flag';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to remove flag:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Feature Flags</h2>
        <button
          onClick={handleToggle}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          {isManaging ? 'Done' : 'Manage Flags'}
        </button>
      </div>

      {isManaging && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
          <div className="flex gap-2 mb-4">
            {appId ? (
              <select
                value={newFlag}
                onChange={(e) => setNewFlag(e.target.value)}
                disabled={catalogLoading || unaddedKeys.length === 0}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {catalogLoading ? (
                  <option value="">Loading flags…</option>
                ) : unaddedKeys.length === 0 ? (
                  <option value="">All flags already added</option>
                ) : (
                  unaddedKeys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))
                )}
              </select>
            ) : (
              <input
                type="text"
                value={newFlag}
                onChange={(e) => setNewFlag(e.target.value)}
                placeholder="Flag key (e.g., wrike_addon)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            <button
              onClick={handleAddFlag}
              disabled={appId ? unaddedKeys.length === 0 : false}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
            >
              Add Flag
            </button>
          </div>
        </div>
      )}

      {flags.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-600">
          No feature flags assigned to this install
        </div>
      ) : (
        <div className="space-y-2">
          {flags.map((flag) => (
            <div
              key={flag.flag_key}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{flag.flag_key}</div>
                <div className="text-xs text-gray-600">
                  Status:{' '}
                  <span className={flag.enabled ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    {flag.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleFlag(flag.flag_key, flag.enabled)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    flag.enabled
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {flag.enabled ? 'Disable' : 'Enable'}
                </button>
                {isManaging && (
                  <button
                    onClick={() => handleRemoveFlag(flag.flag_key)}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
